import { cloneDeep } from "lodash";

type TranslationAudio = {
  NarratorText?: string;
  LanguageDirection?: string;
};
type TranslationNarrationAudio = {
  objectId?: string;
  savedText?: string;
};
type TranslationPageManifest = {
  Audio?: TranslationAudio[];
  narrationAudios?: TranslationNarrationAudio[];
  [key: string]: unknown;
};

type SaveParams = {
  pageManifest: TranslationPageManifest;
  translatedText: string;
  toDirection?: string;
  audioIndex: number;
  isAlternate?: boolean;
  isAllModes?: boolean;
  narrationIndex?: number;
  narrationObjectId?: string;
  updateAudio?: (objectId: string, payload: { savedText: string }) => void;
};

/**
 * Apply translated text into page manifest and optional audio store.
 * This keeps primary vs alternate handling in one place to avoid duplication.
 */
export const applyTranslationSave = ({
  pageManifest,
  translatedText,
  toDirection,
  audioIndex,
  isAlternate = false,
  isAllModes = false,
  narrationIndex,
  narrationObjectId,
  updateAudio,
}: SaveParams) => {
  if (!pageManifest) {
    return pageManifest;
  }

  const newPageManifest = cloneDeep(pageManifest);

  const narrationByIndex =
    narrationIndex !== undefined && narrationIndex >= 0
      ? newPageManifest?.narrationAudios?.[narrationIndex]
      : undefined;
  const narrationById =
    narrationObjectId && newPageManifest?.narrationAudios
      ? newPageManifest.narrationAudios.find((narr) => narr.objectId === narrationObjectId)
      : undefined;
  const narrationAudio = narrationById ?? narrationByIndex;

  // Update narrationAudios (new format)
  if (narrationAudio) {
    narrationAudio.savedText = translatedText;
    if (updateAudio && narrationAudio.objectId) {
      updateAudio(narrationAudio.objectId, { savedText: translatedText });
    }
    return newPageManifest;
  }

  // Legacy Audio array handling
  const indices: number[] = [];

  if (isAllModes) {
    // Primary = first 3, Alternate = last 3
    indices.push(...(isAlternate ? [3, 4, 5] : [0, 1, 2]));
  } else {
    indices.push(audioIndex);
  }

  for (const idx of indices) {
    if (newPageManifest?.Audio?.[idx]) {
      newPageManifest.Audio[idx].NarratorText = translatedText;
      if (toDirection) {
        newPageManifest.Audio[idx].LanguageDirection = toDirection;
      }
    }
  }

  return newPageManifest;
};
